import { Audience, getAudienceFromRoles } from '@phoenix-web/utils/journey'

/**
 * The traits sent to Refiner at identify, and the contract the Refiner dashboard
 * segments on. Deliberately closed: adding a field here is a dashboard-visible
 * change, and leaving it open let `name` type-check after the team decided not
 * to send it.
 *
 * `email` is sent, so Refiner's sync to Customer.io has something to key on, and
 * because the emailed post-payoff NPS survey identifies the same contact by
 * person id and email. The name is still not sent.
 *
 * Refiner traits must be primitives (string / number / date), so `roles` is a
 * delimited string rather than an array.
 */
export interface RefinerUserTraits {
  /** Unique identifier for the user. Required by Refiner, and what the Identity Verification signature is computed from. */
  id: string
  /** The Knock person id, same value as `id`. Sent as its own trait so the dashboard can segment on a named field. */
  person_id: string
  /** Only people with a Knock account have one, so this is optional. */
  account_id?: string
  email?: string
  audience: Audience
  roles: string
  app: string
  environment: string
}

export interface BuildRefinerTraitsInput {
  /** The Knock person id. Everyone surveyed has one, with or without an account. */
  personId: string | number
  accountId?: string | number
  email?: string
  roles?: string[]
  /** `APP_NAME`, e.g. courtyard2 / terrace. */
  app: string
  /** `APP_ENV`, e.g. local / dev / staging / prod. */
  environment: string
}

/**
 * Build the identify traits. Pure, and shared by both apps so the two can't
 * drift apart: the dashboard segments on these, so a difference would fail
 * silently rather than break a build.
 *
 * The contact is keyed on the person id, not the account id, so a person's
 * in-app CSAT responses and their emailed NPS responses (sent by the
 * integrations service, which also identifies by person id) land on one Refiner
 * contact.
 */
export function buildRefinerTraits({
  personId,
  accountId,
  email,
  roles = [],
  app,
  environment,
}: BuildRefinerTraitsInput): RefinerUserTraits {
  return {
    id: String(personId),
    person_id: String(personId),
    ...(accountId != null && { account_id: String(accountId) }),
    ...(email && { email }),
    audience: getAudienceFromRoles(roles),
    // Auth0 doesn't guarantee the order of the roles array, so sort before
    // joining. Otherwise the same user can send `lender,kca-agent` on one visit
    // and `kca-agent,lender` on the next, which reads as two segments.
    roles: [...roles].sort().join(','),
    app,
    environment,
  }
}
